import VariantPicker from '@theme/variant-picker';
import { ThemeEvents, VariantUpdateEvent, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const pageCache = new Map();
const inFlight = new Set();

/** @param {string} href */
function pathOnly(href) {
  try {
    return new URL(href, location.origin).pathname;
  } catch {
    return href;
  }
}

/** @returns {string} */
function cartAddUrl() {
  const url = Theme.routes?.cart_add_url || '/cart/add';
  if (url.endsWith('.js.js')) return url.slice(0, -3);
  if (url.endsWith('.js')) return url;
  return `${url}.js`;
}

/** @param {number} cents */
function formatInr(cents) {
  const amount = cents / 100;
  const text = amount.toLocaleString('en-IN', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `₹${text.replace(/\.00$/, '')}`;
}

/** @param {Element | null | undefined} slot */
function getSlotVariant(slot) {
  const script = slot?.querySelector('variant-picker script[type="application/json"]');
  if (!script?.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

/** @param {Element} container @param {HTMLAnchorElement} active */
function setActiveSwatch(container, active) {
  const scope = container.matches('fieldset.variant-option--color')
    ? container
    : container.querySelector('fieldset.variant-option--color') || container;

  for (const anchor of scope.querySelectorAll('a.variant-option__button-label[href]')) {
    const isActive = anchor === active;
    anchor.classList.toggle('variant-option__button-label--mf-active', isActive);
    anchor.setAttribute('aria-current', isActive ? 'true' : 'false');
  }
}

/** @param {Element} slot @param {string} [title] @param {string} [url] */
function updateSlotTitle(slot, title, url) {
  if (title) slot.setAttribute('data-bundle-title', title);
  const titleEl = slot.querySelector('[data-bundle-slot-title]');
  const mediaLink = slot.querySelector('[data-bundle-slot-media-link]');
  const savedTitle = title ?? slot.getAttribute('data-bundle-title') ?? '';
  if (titleEl && savedTitle) titleEl.textContent = savedTitle;
  if (url && !slot.hasAttribute('data-bundle-fixed-link')) {
    if (titleEl instanceof HTMLAnchorElement) titleEl.href = url;
    if (mediaLink instanceof HTMLAnchorElement) {
      mediaLink.href = url;
      if (savedTitle) mediaLink.setAttribute('aria-label', savedTitle);
    }
  } else if (savedTitle && mediaLink instanceof HTMLAnchorElement) {
    mediaLink.setAttribute('aria-label', savedTitle);
  }
}

/** @param {Element} slot */
function updateSlotPrice(slot) {
  const variant = getSlotVariant(slot);
  const priceEl = slot.querySelector('[data-bundle-slot-price]');
  const compareEl = slot.querySelector('[data-bundle-slot-compare-price]');

  if (priceEl && variant?.price != null) {
    priceEl.textContent = formatInr(variant.price);
  }

  if (compareEl) {
    const compare = variant?.compare_at_price ?? 0;
    const onSale = compare > (variant?.price ?? 0);
    compareEl.textContent = onSale ? formatInr(compare) : '';
    compareEl.hidden = !onSale;
  }
}

/** @param {Document} doc */
function getProductImageFromDoc(doc) {
  const ogImage = doc.querySelector('meta[property="og:image"]')?.getAttribute('content')?.trim();
  if (ogImage) return ogImage;

  const img = doc.querySelector(
    'media-gallery img[src], .product-media-container img[src], .product-information__media img[src]'
  );
  if (img instanceof HTMLImageElement) {
    return img.currentSrc || img.src || '';
  }
  return '';
}

/** @param {Element} slot @param {Document} doc */
function updateSlotThumbnail(slot, doc) {
  const src = getProductImageFromDoc(doc);
  if (!src) return;

  const thumb = slot.querySelector('[data-bundle-slot-thumbnail]');
  if (thumb instanceof HTMLImageElement) {
    thumb.src = src;
    thumb.removeAttribute('srcset');
    return;
  }

  const img = document.createElement('img');
  img.className = 'bundle-product-slot__thumbnail';
  img.setAttribute('data-bundle-slot-thumbnail', '');
  img.src = src;
  img.loading = 'lazy';
  img.alt = slot.getAttribute('data-bundle-title') || '';
  img.width = 150;
  img.height = 220;
  thumb?.replaceWith(img);
}

/** @param {Element} slot @param {string} [title] @param {Document} [doc] @param {string} [url] */
function updateSlotDisplay(slot, title, doc, url) {
  if (title) updateSlotTitle(slot, title, url);
  updateSlotPrice(slot);
  if (doc) updateSlotThumbnail(slot, doc);
}

/** @param {Document} doc @param {Element} sourceDetails */
function getProductTitleFromDoc(doc, sourceDetails) {
  const fromTitle = sourceDetails.querySelector('.pit-title-row__title')?.textContent?.trim();
  if (fromTitle) return fromTitle;
  return doc.querySelector('meta[property="og:title"]')?.getAttribute('content')?.trim() ?? '';
}

/** @param {Element} container @param {string[]} order @param {string} activeHref */
function restoreSwatchOrder(container, order, activeHref) {
  const scope = container.matches('fieldset.variant-option--color')
    ? container
    : container.querySelector('fieldset.variant-option--color') || container;

  const anchors = /** @type {HTMLAnchorElement[]} */ ([...scope.querySelectorAll('a.variant-option__button-label[href]')]);
  const byPath = new Map(anchors.map((a) => [pathOnly(a.href), a]));

  for (const p of order) {
    const anchor = byPath.get(p);
    if (anchor) scope.appendChild(anchor);
  }
  anchors.forEach((a) => !order.includes(pathOnly(a.href)) && scope.appendChild(a));

  const active = anchors.find((a) => pathOnly(a.href) === pathOnly(activeHref));
  if (active instanceof HTMLAnchorElement) {
    setActiveSwatch(container, active);
    updateBundleColorLabel(active);
  }
}

/** @param {string} url */
async function fetchProductDocument(url) {
  const cacheKey = pathOnly(url);
  if (pageCache.has(cacheKey)) return pageCache.get(cacheKey);

  if (inFlight.has(cacheKey)) {
    await new Promise((r) => setTimeout(r, 50));
    return pageCache.get(cacheKey) ?? fetchProductDocument(url);
  }

  inFlight.add(cacheKey);
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    pageCache.set(cacheKey, doc);
    return doc;
  } catch {
    return null;
  } finally {
    inFlight.delete(cacheKey);
  }
}

/** @param {Element | null | undefined} picker */
function stripColorFieldset(picker) {
  picker?.querySelectorAll('fieldset.variant-option--color').forEach((el) => el.remove());
}

/** @param {Element | null | undefined} root */
function stripSizeGuide(root) {
  root?.querySelectorAll('.variant-option__size-guide').forEach((el) => el.remove());
  root?.querySelectorAll('.variant-option-with-guide').forEach((wrap) => {
    const parent = wrap.parentNode;
    if (!parent) return;
    while (wrap.firstChild) parent.insertBefore(wrap.firstChild, wrap);
    wrap.remove();
  });
}

/** @param {Element | null | undefined} slot */
function layoutBundleSizeGuide(slot) {
  if (!slot) return;
  const guide = slot.querySelector('.bundle-size-guide');
  const mutable = slot.querySelector('[data-bundle-slot-mutable]');
  if (!guide || !mutable) return;
  if (guide.parentElement !== mutable) {
    mutable.appendChild(guide);
  }
}

/** @param {Element} root */
function prefetchSiblingProducts(root) {
  for (const anchor of root.querySelectorAll(
    '[data-bundle-slot-colors] a.variant-option__button-label[href]:not(.variant-option__button-label--mf-active)'
  )) {
    if (anchor instanceof HTMLAnchorElement && anchor.href) {
      fetchProductDocument(anchor.href);
    }
  }
}

/** @param {Element | null | undefined} anchor */
function getSwatchColorName(anchor) {
  if (!(anchor instanceof HTMLElement)) return '';

  const fromData = anchor.dataset.colorName?.trim();
  if (fromData) return fromData;

  const fromHidden = anchor.querySelector('.visually-hidden')?.textContent?.trim();
  if (fromHidden) return fromHidden;

  return anchor.getAttribute('aria-label')?.split(' - ')[0]?.trim() ?? '';
}

/** @param {HTMLElement} activeAnchor */
function updateBundleColorLabel(activeAnchor) {
  const label = activeAnchor
    .closest('[data-bundle-slot-colors]')
    ?.querySelector('[data-bundle-color-label]');
  if (!label) return;

  const name = getSwatchColorName(activeAnchor);
  label.textContent = name ? `: ${name}` : '';
}

/** @param {Element} slot */
function initSlotSwatchState(slot) {
  const colorsRoot = slot.querySelector('[data-bundle-slot-colors]');
  if (!colorsRoot) return;

  const active =
    colorsRoot.querySelector('a.variant-option__button-label--mf-active') ??
    colorsRoot.querySelector('a.variant-option__button-label[href]');

  if (active instanceof HTMLAnchorElement) {
    setActiveSwatch(colorsRoot, active);
    updateBundleColorLabel(active);
  }
}

/**
 * Colour swap loads the sibling PDP, which already has that product's gender chart.
 * Keep the bundle dialog, but replace its chart so men/women stay correct.
 * @param {Element | null | undefined} slot
 * @param {Element} sourcePicker
 */
function syncBundleSizeGuide(slot, sourcePicker) {
  const incoming = sourcePicker.querySelector('.cw-size-guide');
  const current = slot?.querySelector('.bundle-size-guide .cw-size-guide');
  if (!incoming || !current) return;

  const fresh = /** @type {HTMLElement} */ (incoming.cloneNode(true));
  delete fresh.dataset.cwSizeGuideReady;
  delete fresh.dataset.cwSizeGuideBusy;
  current.replaceWith(fresh);

  if (typeof window.cwInitSizeGuides === 'function') {
    window.cwInitSizeGuides(slot);
  }
}

/** @param {Element} picker */
function replaceBundleVariantPicker(picker, sourcePicker, slot) {
  const parent = picker.parentElement;
  const newPicker = /** @type {Element} */ (sourcePicker.cloneNode(true));
  if (slot?.querySelector('[data-bundle-slot-colors]')) {
    stripColorFieldset(newPicker);
  }
  if (slot instanceof HTMLElement && slot.dataset.keepSizeGuide !== 'true') {
    stripSizeGuide(newPicker);
  }
  picker.replaceWith(newPicker);
  if (slot instanceof HTMLElement && slot.dataset.keepSizeGuide !== 'true') {
    syncBundleSizeGuide(slot, sourcePicker);
  }
  layoutBundleSizeGuide(slot);
  return /** @type {VariantPicker | null} */ (parent?.querySelector('variant-picker') ?? null);
}

/** @param {Element} bundleRoot */
function updateBundleTotalPrice(bundleRoot) {
  let totalPrice = 0;
  let totalCompare = 0;

  for (const slot of bundleRoot.querySelectorAll('bundle-product-slot')) {
    const variant = getSlotVariant(slot);
    if (!variant) continue;

    totalPrice += variant.price ?? 0;
    const compare = variant.compare_at_price ?? 0;
    totalCompare += compare > variant.price ? compare : variant.price;
  }

  const onSale = totalCompare > totalPrice;
  const discountPercent = onSale ? Math.round(((totalCompare - totalPrice) * 100) / totalCompare) : 0;
  const saleText = formatInr(totalPrice);
  const compareText = formatInr(totalCompare);

  const addToBagPrice = bundleRoot.querySelector('[data-bundle-add-to-bag-price]');
  if (addToBagPrice) {
    addToBagPrice.textContent = saleText;
  }

  const priceRoot = bundleRoot.querySelector('[data-bundle-total-price]');
  const priceContainer = priceRoot?.querySelector('[ref="priceContainer"]');
  if (!priceContainer) return;

  const regular = priceContainer.querySelector('.price__regular');
  const sale = priceContainer.querySelector('.price__sale');

  regular?.classList.toggle('price__hidden', onSale);
  sale?.classList.toggle('price__hidden', !onSale);

  const regularPrice = regular?.querySelector('.price');
  if (regularPrice) regularPrice.textContent = saleText;

  const salePriceEl = sale?.querySelector('.price-item--sale.price');
  const compareEl = sale?.querySelector('.compare-at-price');
  const discountEl = sale?.querySelector('.price-discount-percent');

  if (salePriceEl) salePriceEl.textContent = saleText;
  if (compareEl) compareEl.textContent = compareText;

  if (discountEl) {
    discountEl.textContent = onSale ? `${discountPercent}% OFF` : '';
    discountEl.hidden = !onSale;
  }
}

class BundleProductSlot extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onColorClick, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    if (this.querySelector('[data-bundle-slot-colors]')) {
      stripColorFieldset(this.querySelector('[data-bundle-slot-mutable]'));
    }
    if (this.dataset.keepSizeGuide !== 'true') {
      stripSizeGuide(this);
    }
    layoutBundleSizeGuide(this);
    initSlotSwatchState(this);
    prefetchSiblingProducts(this);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @param {Event} event */
  #onColorClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const anchor = event.target.closest('[data-bundle-slot-colors] a.variant-option__button-label');
    if (!(anchor instanceof HTMLAnchorElement) || !anchor.href) return;

    event.preventDefault();
    event.stopPropagation();

    const colorGroup = anchor.closest('[data-bundle-slot-colors]');
    if (!colorGroup) return;

    this.handleColorClick(anchor, colorGroup);
  };

  /** @param {HTMLAnchorElement} anchor */
  #showGalleryForProduct = async (anchor) => {
    if (this.dataset.updatesGallery !== 'true' || !anchor.href) return;

    const doc = await fetchProductDocument(anchor.href);
    if (!doc) return;

    this.#updateGallery(doc.querySelector('[data-product-grid-content]'));
  };

  /** @param {HTMLAnchorElement} anchor @param {Element} colorGroup */
  handleColorClick = async (anchor, colorGroup) => {
    if (anchor.classList.contains('variant-option__button-label--mf-active')) {
      await this.#showGalleryForProduct(anchor);
      return;
    }

    const fieldset = colorGroup.querySelector('fieldset.variant-option--color') ?? colorGroup;
    const swatchOrder = [...fieldset.querySelectorAll('a.variant-option__button-label[href]')].map((a) =>
      pathOnly(/** @type {HTMLAnchorElement} */ (a).href)
    );

    const bundleRoot = this.closest('bundle-information');
    setActiveSwatch(colorGroup, anchor);
    updateBundleColorLabel(anchor);

    const doc = await fetchProductDocument(anchor.href);
    if (!doc) return;

    const sourceGrid = doc.querySelector('[data-product-grid-content]');
    const sourceDetails = sourceGrid?.querySelector('.product-details');
    if (!sourceDetails) return;

    const productTitle = getProductTitleFromDoc(doc, sourceDetails);

    const currentMutable = this.querySelector('[data-bundle-slot-mutable]');
    if (!currentMutable) return;

    const sourcePicker = sourceDetails.querySelector('variant-picker');
    const currentPicker = currentMutable.querySelector('variant-picker');

    let activePicker = currentPicker;

    if (sourcePicker && currentPicker) {
      activePicker = replaceBundleVariantPicker(currentPicker, sourcePicker, this);

      if (activePicker instanceof VariantPicker) {
        activePicker.updateVariantPickerCss();
      }

      if (activePicker instanceof HTMLElement && sourcePicker.dataset.productId) {
        activePicker.dataset.productId = sourcePicker.dataset.productId;
        this.dataset.productId = sourcePicker.dataset.productId;
      }
      if (activePicker instanceof HTMLElement && sourcePicker.dataset.productUrl) {
        activePicker.dataset.productUrl = sourcePicker.dataset.productUrl;
      }
    }

    this.setAttribute('data-bundle-title', productTitle);
    updateSlotDisplay(this, productTitle, doc, anchor.href);

    const sourceVariantScript = sourcePicker?.querySelector('script[type="application/json"]');
    const currentVariantScript = activePicker?.querySelector('script[type="application/json"]');
    if (sourceVariantScript?.textContent && currentVariantScript) {
      currentVariantScript.textContent = sourceVariantScript.textContent;
    }

    const variantInput =
      sourceDetails.querySelector('[data-bundle-variant-input]') ?? doc.querySelector('[data-bundle-variant-input]');
    const currentInput = currentMutable.querySelector('[data-bundle-variant-input]');
    if (variantInput instanceof HTMLInputElement && currentInput instanceof HTMLInputElement) {
      currentInput.value = variantInput.value;
      currentInput.dataset.productId = sourcePicker?.dataset.productId ?? variantInput.dataset.productId ?? '';
    } else {
      const json = sourcePicker?.querySelector('script[type="application/json"]')?.textContent;
      if (json && currentInput instanceof HTMLInputElement) {
        try {
          currentInput.value = JSON.parse(json).id ?? '';
        } catch {
          /* ignore */
        }
      }
    }

    if (this.dataset.productId !== undefined && sourcePicker?.dataset.productId) {
      this.dataset.productId = sourcePicker.dataset.productId;
    }

    restoreSwatchOrder(colorGroup, swatchOrder, anchor.href);

    if (bundleRoot) {
      if (this.dataset.updatesGallery === 'true') this.#updateGallery(sourceGrid);
      updateBundleColorLabel(anchor);
    }

    this.#syncVariantInput();
    if (bundleRoot) updateBundleTotalPrice(bundleRoot);
    this.#updateBuyButtons();
    prefetchSiblingProducts(this);
  };

  /** @param {Element | null | undefined} sourceGrid */
  #updateGallery(sourceGrid) {
    const bundleRoot = this.closest('bundle-information');
    const mediaWrap = bundleRoot?.querySelector('.product-information__media');
    const newGallery = sourceGrid?.querySelector('media-gallery');
    const currentGallery = mediaWrap?.querySelector('media-gallery');

    if (newGallery && currentGallery) {
      currentGallery.replaceWith(/** @type {Element} */ (newGallery.cloneNode(true)));
    }
  }

  /** @param {VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    if (event.detail.data.productId && event.detail.data.productId !== this.dataset.productId) return;

    event.stopPropagation();

    const resource = event.detail.resource;
    if (resource?.id) {
      const script = this.querySelector('variant-picker script[type="application/json"]');
      if (script) script.textContent = JSON.stringify(resource);
    }

    if (this.querySelector('[data-bundle-slot-colors]')) {
      stripColorFieldset(this.querySelector('[data-bundle-slot-mutable]'));
    }
    if (this.dataset.keepSizeGuide !== 'true') {
      stripSizeGuide(this);
    }
    layoutBundleSizeGuide(this);

    this.#syncVariantInput(resource?.id);
    updateSlotPrice(this);

    const bundleRoot = this.closest('bundle-information');
    if (bundleRoot) updateBundleTotalPrice(bundleRoot);
    this.#updateBuyButtons();
  };

  #onVariantSelected = () => {
    updateSlotPrice(this);
    const bundleRoot = this.closest('bundle-information');
    if (bundleRoot) updateBundleTotalPrice(bundleRoot);
    this.#updateBuyButtons();
  };

  /** @param {string} [variantId] */
  #syncVariantInput(variantId) {
    const input = this.querySelector('[data-bundle-variant-input]');
    const picker = this.querySelector('variant-picker');
    const selected = picker?.querySelector('input:checked[data-variant-id], select option[selected][data-variant-id]');
    const id = variantId ?? selected?.getAttribute('data-variant-id') ?? '';

    if (input instanceof HTMLInputElement && id) {
      input.value = id;
    }
  }

  #updateBuyButtons() {
    this.closest('bundle-information')?.querySelector('bundle-buy-buttons')?.updateState?.();
  }
}

class BundleBuyButtons extends HTMLElement {
  #minItems() {
    return Number(this.dataset.minItems) || 2;
  }

  connectedCallback() {
    this.querySelector('.bundle-buy-buttons__add-to-cart')?.addEventListener('click', this.#addToCart);
    this.querySelector('.bundle-buy-buttons__buy-now')?.addEventListener('click', this.#buyNow);
    this.updateState();
  }

  disconnectedCallback() {
    this.querySelector('.bundle-buy-buttons__add-to-cart')?.removeEventListener('click', this.#addToCart);
    this.querySelector('.bundle-buy-buttons__buy-now')?.removeEventListener('click', this.#buyNow);
  }

  /** @returns {string[]} */
  #getCartSectionIds() {
    return [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
          .filter((id) => Boolean(id))
      ),
    ];
  }

  /** @returns {{ variantId: string, available: boolean }[]} */
  #getLineItems() {
    const root = this.closest('bundle-information');
    /** @type {{ variantId: string, available: boolean }[]} */
    const items = [];

    for (const slot of root?.querySelectorAll('bundle-product-slot') ?? []) {
      const input = slot.querySelector('[data-bundle-variant-input]');
      const variantId = input instanceof HTMLInputElement ? input.value : '';
      const picker = slot.querySelector('variant-picker input:checked, variant-picker select option[selected]');
      const available = picker?.getAttribute('data-option-available') !== 'false';
      if (variantId) items.push({ variantId, available });
    }

    return items;
  }

  /** @param {boolean} disabled */
  #setButtonsDisabled(disabled) {
    for (const button of this.querySelectorAll(
      '.bundle-buy-buttons__add-to-cart, .bundle-buy-buttons__buy-now'
    )) {
      if (button instanceof HTMLButtonElement) button.disabled = disabled;
    }
  }

  updateState() {
    const items = this.#getLineItems();
    const hasRequired = items.length >= this.#minItems() && items.every((i) => i.variantId);
    const canAdd = hasRequired && items.every((i) => i.available);
    const isSoldOut = hasRequired && !canAdd;

    const buyNow = this.querySelector('.bundle-buy-buttons__buy-now');
    const addToCart = this.querySelector('.bundle-buy-buttons__add-to-cart');
    const soldOut = this.querySelector('.bundle-buy-buttons__sold-out');
    const buttonsWrap = this.querySelector('.product-form-buttons--bundle');

    if (canAdd) {
      buyNow?.removeAttribute('hidden');
      addToCart?.removeAttribute('hidden');
      soldOut?.setAttribute('hidden', '');
      buttonsWrap?.classList.remove('product-form-buttons--sold-out');
      this.#setButtonsDisabled(false);
      return;
    }

    buyNow?.setAttribute('hidden', '');
    addToCart?.setAttribute('hidden', '');

    if (isSoldOut) {
      soldOut?.removeAttribute('hidden');
      buttonsWrap?.classList.add('product-form-buttons--sold-out');
    } else {
      soldOut?.setAttribute('hidden', '');
      buttonsWrap?.classList.remove('product-form-buttons--sold-out');
    }

    this.#setButtonsDisabled(true);
  }

  /**
   * @param {{ goToCheckout?: boolean }} [options]
   */
  #addItems = async (options = {}) => {
    const items = this.#getLineItems();
    if (items.length < this.#minItems() || !items.every((i) => i.available)) return false;

    this.#setButtonsDisabled(true);

    try {
      const sectionIds = options.goToCheckout ? [] : this.#getCartSectionIds();
      const payload = {
        items: items.map((i) => ({ id: Number(i.variantId), quantity: 1 })),
        ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
      };

      const response = await fetch(cartAddUrl(), {
        ...fetchConfig('json'),
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || data.status) return false;

      if (options.goToCheckout) {
        window.location.href = '/checkout';
        return true;
      }

      const cart = await fetch('/cart.js').then((res) => res.json());

      document.dispatchEvent(
        new CartAddEvent(cart, this.dataset.sectionId ?? '', {
          source: 'bundle-buy-buttons',
          itemCount: cart.item_count,
          sections: data.sections,
        })
      );
      this.closest('dialog-component')?.closeDialog?.();
      return true;
    } catch {
      return false;
    } finally {
      if (!options.goToCheckout) this.updateState();
    }
  };

  #addToCart = async () => {
    await this.#addItems();
  };

  #buyNow = async () => {
    await this.#addItems({ goToCheckout: true });
    this.updateState();
  };
}

class BundleInformation extends HTMLElement {
  connectedCallback() {
    for (const slot of this.querySelectorAll('bundle-product-slot')) {
      if (slot.querySelector('[data-bundle-slot-colors]')) {
        stripColorFieldset(slot.querySelector('[data-bundle-slot-mutable]'));
      }
      if (slot instanceof HTMLElement && slot.dataset.keepSizeGuide !== 'true') {
        stripSizeGuide(slot);
      }
      layoutBundleSizeGuide(slot);
    }
    updateBundleTotalPrice(this);
  }
}

if (!customElements.get('bundle-product-slot')) {
  customElements.define('bundle-product-slot', BundleProductSlot);
}

if (!customElements.get('bundle-buy-buttons')) {
  customElements.define('bundle-buy-buttons', BundleBuyButtons);
}
if (!customElements.get('bundle-information')) {
  customElements.define('bundle-information', BundleInformation);
}
